/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import inventoryApi from '../../../services/inventoryApi';
import InventoryLayout, { 
  Card, 
  CardHeader, 
  ActionButton, 
  ErrorState,
  StatusBadge
} from '../../../components/admin/InventoryLayout';
import UpdateStockModal from './components/UpdateStockModal';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stockModalOpen, setStockModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadProductData();
    }
  }, [id]);

  const loadProductData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!id || isNaN(id)) {
        throw new Error('Invalid product ID');
      }
      
      const response = await inventoryApi.items.getItem(id);
      const productData = response.item || response;
      
      if (!productData || !productData.id) {
        throw new Error('Product not found');
      }
      
      setProduct(productData);
      
    } catch (err) {
      console.error('Error loading product:', err);
      setError(err.message || 'Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (stock, reorderPoint) => {
    if (stock <= 0) {
      return { status: 'Out of Stock', variant: 'danger' };
    } else if (stock <= reorderPoint) {
      return { status: 'Low Stock', variant: 'warning' };
    } else {
      return { status: 'In Stock', variant: 'success' };
    }
  };

  if (loading) {
    return (
      <InventoryLayout 
        title="Product Details"
        subtitle="Loading product information..."
        currentTab="products"
      >
        <Card>
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7d6633] mx-auto mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400">Loading product details...</p>
          </div>
        </Card>
      </InventoryLayout>
    );
  }

  if (error || !product) {
    return (
      <InventoryLayout 
        title="Product Not Found"
        subtitle="Unable to load product information"
        currentTab="products"
      >
        <ErrorState 
          error={error || 'Product not found'} 
          onRetry={loadProductData}
        />
      </InventoryLayout>
    );
  }

  const stockInfo = getStockStatus(product.totalStock || 0, product.reorderPoint || 0);

  return (
    <InventoryLayout 
      title={product.name}
      subtitle={`${product.brand ? `${product.brand} • ` : ''}${product.category || 'Product'}`}
      currentTab="products"
      action={
        <div className="flex gap-2">
          <ActionButton
            onClick={() => setStockModalOpen(true)}
            variant="accent"
            size="md"
            className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
          >
            Update Stock
          </ActionButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Product Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Overview */}
          <Card>
            <CardHeader
              title="Product Overview"
              icon={
                <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-6">
                {/* Product Image */}
                <div className="flex-shrink-0">
                  {product.imageUrl ? (
                    <img 
                      src={product.imageUrl} 
                      alt={product.name}
                      className="w-32 h-32 object-cover rounded-xl border border-white/20 dark:border-white/10"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-white/20 dark:bg-zinc-800/20 border border-white/20 dark:border-white/10 rounded-xl flex items-center justify-center">
                      <svg className="h-12 w-12 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
                
                {/* Product Details */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-2">
                    <StatusBadge 
                      status={product.active ? 'Active' : 'Archived'} 
                      variant={product.active ? 'success' : 'default'} 
                    />
                    <StatusBadge 
                      status={stockInfo.status} 
                      variant={stockInfo.variant} 
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">SKU:</span>
                      <span className="ml-2 text-zinc-900 dark:text-zinc-100 font-medium">
                        {product.sku || 'Not set'}
                      </span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Unit:</span>
                      <span className="ml-2 text-zinc-900 dark:text-zinc-100 font-medium">
                        {product.unit || 'Not set'}
                      </span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Category:</span>
                      <span className="ml-2 text-zinc-900 dark:text-zinc-100 font-medium">
                        {product.category || 'Not set'}
                      </span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Lead Time:</span>
                      <span className="ml-2 text-zinc-900 dark:text-zinc-100 font-medium">
                        {product.reorderLeadTimeDays || 7} days
                      </span>
                    </div>
                  </div>
                  
                  {product.description && (
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400 text-sm">Description:</span>
                      <p className="text-zinc-900 dark:text-zinc-100 mt-1">{product.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Stock Updates"
              subtitle="Add stock when new products arrive"
              icon={
                <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            />
            <div className="p-4 md:p-6">
              <div className="rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 p-4 text-center">
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Keep this product stocked by adding new inventory.
                </p>
                <ActionButton
                  onClick={() => setStockModalOpen(true)}
                  variant="accent"
                  size="md"
                  className="mt-4 !bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
                >
                  Update Stock
                </ActionButton>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Inventory Status */}
          <Card>
            <CardHeader
              title="Current Inventory"
              icon={
                <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              }
            />
            
            <div className="p-4 md:p-6 space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
                  {product.totalStock || 0}
                </div>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  {product.unit ? `${product.unit}s` : 'units'} in stock
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Reorder Point:</span>
                  <span className="text-zinc-900 dark:text-zinc-100 font-medium">
                    {product.reorderPoint || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Preferred Stock:</span>
                  <span className="text-zinc-900 dark:text-zinc-100 font-medium">
                    {product.preferredStock || 0}
                  </span>
                </div>
              </div>
              
              {/* Stock Management Actions */}
              <div className="pt-4 border-t border-white/10 space-y-2">
                <ActionButton
                  onClick={() => setStockModalOpen(true)}
                  variant="accent"
                  size="md"
                  className="w-full !bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Update Stock
                </ActionButton>
              </div>
            </div>
          </Card>

          {/* Supplier Information */}
          {product.defaultSupplier && (
            <Card>
              <CardHeader
                title="Default Supplier"
                icon={
                  <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                }
              />
              
              <div className="p-4 md:p-6 space-y-3">
                <div>
                  <div className="font-medium text-zinc-900 dark:text-zinc-100">
                    {product.defaultSupplier.name}
                  </div>
                  {product.defaultSupplier.contactPerson && (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                      Contact: {product.defaultSupplier.contactPerson}
                    </div>
                  )}
                </div>
                
                {product.defaultSupplier.email && (
                  <div className="text-sm">
                    <span className="text-zinc-500 dark:text-zinc-400">Email:</span>
                    <a 
                      href={`mailto:${product.defaultSupplier.email}`}
                      className="ml-2 text-[#7d6633] hover:underline"
                    >
                      {product.defaultSupplier.email}
                    </a>
                  </div>
                )}
                
                {product.defaultSupplier.phone && (
                  <div className="text-sm">
                    <span className="text-zinc-500 dark:text-zinc-400">Phone:</span>
                    <a 
                      href={`tel:${product.defaultSupplier.phone}`}
                      className="ml-2 text-[#7d6633] hover:underline"
                    >
                      {product.defaultSupplier.phone}
                    </a>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader
              title="Quick Actions"
              icon={
                <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
            />
            
            <div className="p-4 md:p-6 space-y-2">
              <ActionButton
                onClick={() => navigate(`/admin/inventory/products/${id}/edit`)}
                variant="secondary"
                size="md"
                className="w-full"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Product
              </ActionButton>
              <ActionButton
                onClick={() => navigate('/admin/inventory/products')}
                variant="secondary"
                size="md"
                className="w-full"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Products
              </ActionButton>
            </div>
          </Card>
        </div>
      </div>

      <UpdateStockModal
        isOpen={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        onSuccess={loadProductData}
        item={{ id: product.id, name: product.name, totalStock: product.totalStock || 0 }}
      />
    </InventoryLayout>
  );
};

export default ProductDetail;